'use client';

import { CloseIcon, SearchIcon } from '@repo/icons';
import { CurrencyIcon } from '@repo/ui/currency-icon';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@repo/ui/dialog';
import { Input } from '@repo/ui/input';
import { NoData } from '@repo/ui/no-data';
import { filterInData } from '@repo/utils/filter-in-data';
import { useTranslations } from 'next-intl';
import React, { memo, useMemo, useState } from 'react';
import { type ViewType } from '@/app/[locale]/(account)/account/deposit';
import Method from '@/components/common/method-item';
import { type FullMethod, usePaymentMethodsWithNetworks } from '@/hooks/usePaymentMethodsWithNetworks';
import { type PaymentTypes } from '@/server-actions/payment-methods';

type OtherCoinsModalProps = {
  paymentType: PaymentTypes;
  setView: (view: ViewType) => void;
  selectedMethod?: FullMethod;
  setSelectedMethod?: (method?: FullMethod) => void;
};

const VIEW_BY_PAYMENT_TYPE = {
  CRYPTO: 'CRYPTO_DETAILS',
  CREDIT_CARD: 'FIAT',
};

const PaymentMethodsModal = ({ paymentType, setView, selectedMethod, setSelectedMethod }: OtherCoinsModalProps) => {
  const t = useTranslations(paymentType);
  const [searchValue, setSearchValue] = useState('');

  const { topMethods, otherMethods } = usePaymentMethodsWithNetworks({ paymentType });
  const allMethods = useMemo(() => [...topMethods, ...otherMethods], [topMethods, otherMethods]);
  const filteredMethods = useMemo(
    () => filterInData(allMethods, ['currencyCode'], searchValue),
    [allMethods, searchValue]
  ) as FullMethod[];

  const methodsToShow = searchValue ? filteredMethods : allMethods;

  const firstMethod = !searchValue ? methodsToShow[0] : null;
  const restMethods = searchValue ? methodsToShow : methodsToShow.slice(1);

  const renderMethod = (item: FullMethod) => (
    <Method
      key={`${item.providerId}-${item.currencyCode}`}
      title={item.currencyCode}
      subTitle={item.networkOptions?.map((n) => n.label).join(', ') || item.currencyCode}
      onClick={() => {
        setSelectedMethod?.(item);
        setView('CRYPTO_DETAILS');
      }}
      isActive={item.currencyCode === selectedMethod?.currencyCode}
      icons={[
        <CurrencyIcon
          key={`${item.providerId}-${item.currencyCode}`}
          currency={item.currencyCode}
        />,
      ]}
    />
  );

  return (
    <Dialog
      open
      onOpenChange={() => {
        const view = VIEW_BY_PAYMENT_TYPE?.[selectedMethod?.paymentType!] ?? 'CREDIT_CARDS';
        setView(view as ViewType);
        setSelectedMethod?.(selectedMethod);
      }}
    >
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="z-[var(--z-payment-methods-modal)] h-auto w-full max-w-[90%] gap-0 self-center rounded-xl sm:max-w-[420px]"
        portalOverlayClassName="z-[var(--z-payment-methods-modal-overlay)]"
      >
        <DialogHeader className="h-12 w-full flex-row items-center justify-between space-y-0 px-3 vt-surface-lighter-bg">
          <DialogTitle className="vt-font-highlight-font-family vt-body-text-darker">
            {t('other-coins-modal-title')}
          </DialogTitle>
          <DialogTrigger asChild>
            <div className="cursor-pointer">
              <CloseIcon className="vt-body-text-darker" />
            </div>
          </DialogTrigger>
        </DialogHeader>
        <div className="min-h-[400px] w-full space-y-3 p-[16px] vt-surface-default-bg sm:min-h-[450px]">
          <Input
            placeholder={t('search-payment-method-placeholder')}
            value={searchValue}
            icon={<SearchIcon />}
            onChange={({ target: { value } }) => setSearchValue(value)}
          />

          {methodsToShow.length === 0 ? (
            <NoData
              title={t('no-results')}
              className="p-3 vt-containers-settings-box-light-bg"
            />
          ) : (
            <>
              {firstMethod && renderMethod(firstMethod)}
              <div className="grid grid-cols-2 gap-3">{restMethods.map(renderMethod)}</div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default memo(PaymentMethodsModal);
